import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaCheck, FaTimes, FaDownload, FaArrowLeft } from 'react-icons/fa';

export default function RegularizeRequestsList() {
     const [requests, setRequests] = useState([]);
     const [, setLoading] = useState(false);
     const [actionLoading, setActionLoading] = useState(false);
     const [rejectId, setRejectId] = useState(null);
     const [rejectRemarks, setRejectRemarks] = useState('');
     const BASE_URL = process.env.REACT_APP_BASE_URL;
     const token = sessionStorage.getItem('token');
     const navigate = useNavigate();
     const [status, setStatus] = useState('pending'); // pending|approved|rejected|my|all
     const [page, setPage] = useState(1);
     const [perPage, setPerPage] = useState(10);
     const [total, setTotal] = useState(0);
     const [totalPages, setTotalPages] = useState(1);
     const [search, setSearch] = useState('');
     const [searchInput, setSearchInput] = useState('');
     const searchDebounce = useRef(null);
     // Accept role from sessionStorage or localStorage and normalize case for comparison
     const userRole = sessionStorage.getItem('user_role') || sessionStorage.getItem('role') || sessionStorage.getItem('userRole') || localStorage.getItem('userRole') || localStorage.getItem('user_role') || localStorage.getItem('role') || 'employee';
     const userCode = sessionStorage.getItem('user_code') || sessionStorage.getItem('userCode') || localStorage.getItem('user_code') || localStorage.getItem('userCode') || '';
     const canApprove = ['admin', 'super_admin', 'hod', 'hr', 'reporting_manager'].includes(String(userRole || '').toLowerCase());
     // Debug info to help verify role detection in the browser console
     if (process.env.NODE_ENV !== 'production') console.debug('RegularizeRequestsList role', { userRole, userCode, canApprove });

     // Thumbnails cache for list view
     const [thumbs, setThumbs] = useState({}); // { [requestId]: { url, failed } }
     const thumbControllers = useRef({});
     const createdObjectUrls = useRef(new Set()); // keep object URLs in a ref across renders

     const [viewerOpen, setViewerOpen] = useState(false);
     const [viewerUrl, setViewerUrl] = useState(null);
     const [viewerContentType, setViewerContentType] = useState('');
     const [viewerName, setViewerName] = useState('');

     // Format date like '07 Jan, 2026' or return raw if unparsable
     const formatDate = (dateString) => {
          if (!dateString) return '-';
          try {
               const d = new Date(dateString);
               if (Number.isNaN(d.getTime())) return String(dateString);
               return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
          } catch (e) {
               return String(dateString);
          }
     }; 

     React.useEffect(() => {
          const _urls = createdObjectUrls.current;
          return () => { try { _urls.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} }); } catch (e) {} };
     }, []);

     // Cleanup debounce timer
     useEffect(() => {
          return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
     }, []);

     // Fetch small thumbnails for image attachments (list view)
     useEffect(() => {
          // abort previous controllers
          Object.values(thumbControllers.current || {}).forEach(ac => { try { ac.abort(); } catch (e) { } });
          thumbControllers.current = {};

          requests.forEach(r => {
               if (!r || thumbs[r.id]) return;
               // prefer attachments[] if present, otherwise fallback to legacy fields
               const firstAtt = Array.isArray(r.attachments) && r.attachments.length ? r.attachments[0] : null;
               const nm = r.original_name || (firstAtt && firstAtt.original_name) || r.attachment || r.attachment_path || r.attachmentPath || '';
               const ext = (nm.split('.').pop() || '').toLowerCase();
               if (!['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(ext)) return;

               const ac = new AbortController();
               thumbControllers.current[r.id] = ac;

               (async () => {
                    try {
                         const t = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
                         const attPath = (firstAtt && (firstAtt.file_path || firstAtt.path)) || r.attachment || r.attachment_path || r.attachmentPath;
                         if (!attPath) { setThumbs(prev => ({ ...prev, [r.id]: { failed: true } })); return; }
                         const fetchUrl = (attPath.indexOf('uploads/regularize') !== -1) ? `${BASE_URL}/regularize/download/${encodeURIComponent(attPath.split('/').pop())}` : `${BASE_URL}/${attPath}`;
                         const res = await fetch(fetchUrl, { headers: t ? { Authorization: `Bearer ${t}` } : {}, signal: ac.signal });
                         if (process.env.NODE_ENV !== 'production') console.debug('Thumbnail fetch (list)', { fetchUrl, ok: res.ok, status: res.status, contentType: res.headers.get('content-type') });
                         if (!res.ok) { setThumbs(prev => ({ ...prev, [r.id]: { failed: true } })); return; }
                         let ct = (res.headers.get('content-type') || '').toLowerCase();
                         // if server didn't set a helpful content-type, fall back to extension map
                         if (!ct || !ct.startsWith('image/')) {
                              const nmUsed = nm || (firstAtt && firstAtt.original_name) || '';
                              const extMap = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', svg: 'image/svg+xml' };
                              const extLocal = (nmUsed.split('.').pop() || '').toLowerCase();
                              if (extLocal && extMap[extLocal]) {
                                   ct = extMap[extLocal];
                              } else {
                                   setThumbs(prev => ({ ...prev, [r.id]: { failed: true } }));
                                   return;
                              }
                         }

                         const ab = await res.arrayBuffer();
                         const b = new Blob([ab], { type: ct });
                         const url = URL.createObjectURL(b);
                         createdObjectUrls.current.add(url);
                         setThumbs(prev => ({ ...prev, [r.id]: { url } }));
                    } catch (e) {
                         if (e && e.name === 'AbortError') return;
                         console.warn('Thumbnail fetch failed', r.id, e && e.message);
                         setThumbs(prev => ({ ...prev, [r.id]: { failed: true } }));
                    } finally {
                         try { delete thumbControllers.current[r.id]; } catch (e) { }
                    }
               })();
          });

          return () => { Object.values(thumbControllers.current || {}).forEach(ac => { try { ac.abort(); } catch (e) { } }); thumbControllers.current = {}; };
     }, [requests, BASE_URL, thumbs]);



     const closeViewer = () => {
          setViewerOpen(false);
          try { if (viewerUrl) { URL.revokeObjectURL(viewerUrl); createdObjectUrls.current.delete(viewerUrl); } } catch (e) { }
          setViewerUrl(null);
          setViewerContentType('');
          setViewerName('');
     };

     // Preview modal state
     const [previewOpen, setPreviewOpen] = useState(false);
     const [selectedRequest, setSelectedRequest] = useState(null);
     const [previewLoading, setPreviewLoading] = useState(false);
     const [previewThumbs, setPreviewThumbs] = useState({}); // { [idx]: { url, failed } }
     const previewControllers = useRef({});

     const openRequestPreview = async (r) => {
          // open modal immediately with a loading state, then fetch full details
          try {
               setPreviewOpen(true);
               setSelectedRequest(null);
               setPreviewLoading(true);
               setPreviewThumbs({});

               const t = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
               const res = await fetch(`${BASE_URL}/regularize/${r.id}`, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
               if (!res.ok) throw new Error('Failed to load request details');
               const payload = await res.json();
               const req = Array.isArray(payload) ? payload[0] : payload.data || payload;
               // Merge attachments from response if present (payload.attachments)
               if (payload && payload.attachments && Array.isArray(payload.attachments)) {
                    req.attachments = payload.attachments;
               }
               setSelectedRequest(req || r);

               // Prefetch attachment thumbnails (images only)
               const attachments = Array.isArray(req?.attachments) && req.attachments.length ? req.attachments : (req.attachment || req.attachment_path || req.attachmentPath) ? [{ file_path: req.attachment || req.attachment_path || req.attachmentPath, original_name: req.attachment_name || req.original_name }] : [];
               if (attachments.length) {
                    attachments.forEach((a, idx) => {
                         const ac = new AbortController();
                         previewControllers.current[idx] = ac;
                         (async () => {
                              try {
                                   const apRaw = a.file_path || a.path || a.attachment_path || a.attachment || '';
                                   if (!apRaw) { setPreviewThumbs(prev => ({ ...prev, [idx]: { failed: true } })); return; }
                                   const ap = (apRaw.indexOf('uploads/regularize') !== -1) ? `${BASE_URL}/regularize/download/${encodeURIComponent(apRaw.split('/').pop())}` : `${BASE_URL}/${apRaw}`;
                                   const res2 = await fetch(ap, { headers: t ? { Authorization: `Bearer ${t}` } : {}, signal: ac.signal });
                                   if (!res2.ok) { setPreviewThumbs(prev => ({ ...prev, [idx]: { failed: true } })); return; }
                                   const ct = (res2.headers.get('content-type') || '').toLowerCase();
                                   if (!ct.startsWith('image/')) { setPreviewThumbs(prev => ({ ...prev, [idx]: { failed: true } })); return; }
                                   const ab = await res2.arrayBuffer();
                                   const b = new Blob([ab], { type: ct });
                                   const url = URL.createObjectURL(b);
                                   createdObjectUrls.current.add(url);
                                   setPreviewThumbs(prev => ({ ...prev, [idx]: { url } }));
                              } catch (e) {
                                   if (e && e.name === 'AbortError') return;
                                   console.warn('Preview attachment fetch failed', e && e.message);
                                   setPreviewThumbs(prev => ({ ...prev, [idx]: { failed: true } }));
                              } finally {
                                   try { delete previewControllers.current[idx]; } catch (e) { }
                              }
                         })();
                    });
               }
          } catch (err) {
               console.error('Failed to open preview', err);
               alert('Failed to load request details');
               setPreviewOpen(false);
          } finally {
               setPreviewLoading(false);
          }
     };

     const closeRequestPreview = () => {
          // abort any ongoing preview fetches and clean up created object URLs
          Object.values(previewControllers.current || {}).forEach(ac => { try { ac.abort(); } catch (e) { } });
          previewControllers.current = {};
          // revoke any preview object URLs
          Object.values(previewThumbs || {}).forEach(t => { try { if (t && t.url) { URL.revokeObjectURL(t.url); createdObjectUrls.current.delete(t.url); } } catch (e) { } });
          setPreviewThumbs({});
          setPreviewOpen(false);
          setSelectedRequest(null);
     };

     // Approve from modal and close it
     const approveAndClose = async (id) => {
          await approve(id);
          closeRequestPreview();
     };

     // When rejecting from modal, close preview and open reject modal
     const rejectFromPreview = (id) => {
          closeRequestPreview();
          startReject(id);
     };

     // Download protected attachment using Authorization header then open blob in new tab
     const handleDownload = async (filePath, suggestedName) => {
          try {
               // Build URL to protected download endpoint when file path points to uploads; otherwise treat as direct path
               let url;
               if (!filePath) throw new Error('No file specified');
               if (typeof filePath === 'string' && (filePath.startsWith('http://') || filePath.startsWith('https://') || filePath.includes('/regularize/download/'))) {
                    url = filePath;
               } else if (typeof filePath === 'string' && filePath.indexOf('uploads/regularize') !== -1) {
                    const name = filePath.split('/').pop();
                    url = `${BASE_URL}/regularize/download/${encodeURIComponent(name)}`;
               } else {
                    url = `${BASE_URL}/${filePath}`;
               }

               // Try multiple token storage locations for maximum compatibility
               const t = sessionStorage.getItem('token') || localStorage.getItem('token') || sessionStorage.getItem('authToken') || localStorage.getItem('authToken') || '';
               const headers = { Authorization: t ? `Bearer ${t}` : undefined };

               console.debug('Downloading attachment', { url, tokenPresent: !!t });

               const res = await fetch(url, { method: 'GET', headers });
               if (process.env.NODE_ENV !== 'production') console.debug('Attachment download response (list)', { url, ok: res.ok, status: res.status, contentType: res.headers.get('content-type') });
               if (!res.ok) {
                    let bodyText = await res.text().catch(() => null);
                    let msg = res.statusText || 'Failed to download file';
                    try {
                         const parsed = bodyText ? JSON.parse(bodyText) : null;
                         msg = parsed?.message || parsed?.error || bodyText || msg;
                    } catch (e) { msg = bodyText || msg; }
                    console.warn('Attachment download failed', res.status, msg);
                    alert(`Download failed (${res.status}): ${msg}`);
                    return;
               }
               // Build Blob with response content-type so images and PDFs render inline
               const contentType = res.headers.get('content-type') || '';
               const arrayBuffer = await res.arrayBuffer();
               const blob = new Blob([arrayBuffer], { type: contentType });
               const blobUrl = URL.createObjectURL(blob);
               createdObjectUrls.current.add(blobUrl);

               // Open directly in new tab - let browser handle the display natively
               if (contentType.startsWith('image/') || contentType.startsWith('application/pdf')) {
                    // Open the blob URL directly in a new tab
                    window.open(blobUrl, '_blank');
               } else {
                    // For other file types, trigger download
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = suggestedName || filePath.split('/').pop();
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
               }
          } catch (err) {
               console.error('Download failed', err);
               alert('Failed to download attachment: ' + (err && err.message ? err.message : String(err)));
          }
     };

     const fetchRequests = React.useCallback(async (s = status, p = page, pp = perPage, q = search) => {
          setLoading(true);
          try {
               // Unified call to list endpoint supports status=my/pending/all and accepts page/per_page/search
               const params = new URLSearchParams();
               params.set('status', s);
               params.set('page', p);
               params.set('per_page', pp);
               if (q && q.trim()) params.set('search', q.trim());

               const url = `${BASE_URL}/regularize/list?${params.toString()}`;

               const res = await fetch(url, {
                    headers: { Authorization: token ? `Bearer ${token}` : undefined },
               });
               if (!res.ok) throw new Error('Failed to fetch');
               const payload = await res.json();
               const requestsData = Array.isArray(payload) ? payload : payload.data || payload;
               setRequests(requestsData || []);

               // Update pagination meta if present
               const meta = payload && payload.meta ? payload.meta : null;
               if (meta) {
                    setTotal(meta.total ?? 0);
                    setPerPage(meta.per_page ?? pp);
                    setPage(meta.page ?? p);
                    setTotalPages(Math.max(1, Math.ceil((meta.total ?? 0) / (meta.per_page ?? pp))));
               } else {
                    setTotal((requestsData && requestsData.length) || 0);
                    setTotalPages(Math.max(1, Math.ceil(((requestsData && requestsData.length) || 0) / pp)));
               }

               // clear thumbs cache so thumbnails are refetched for new lists
               setThumbs({});
          } catch (err) {
               console.error(err);
               alert('Failed to load requests');
          } finally {
               setLoading(false);
          }
     }, [BASE_URL, token, status, page, perPage, search]);

     // whenever status changes, reset to first page
     useEffect(() => { setPage(1); }, [status]);

     // fetch when any pagination / filters change
     useEffect(() => { fetchRequests(status, page, perPage, search); }, [status, page, perPage, search, fetchRequests]);

     const approve = async (id) => {
          if (!canApprove) return alert('Not authorized');
          if (!window.confirm('Approve this request?')) return;
          setActionLoading(true);
          try {
               const res = await fetch(`${BASE_URL}/regularize/${id}/approve`, {
                    method: 'POST',
                    headers: { Authorization: token ? `Bearer ${token}` : undefined }
               });
               if (!res.ok) throw new Error('Approve failed');
               await fetchRequests(status);
          } catch (err) {
               console.error(err);
               alert(err.message || 'Approve failed');
          } finally { setActionLoading(false); }
     };

     const startReject = (id) => { setRejectId(id); setRejectRemarks(''); };
     const submitReject = async () => {
          if (!rejectRemarks.trim()) return alert('Please enter remarks');
          setActionLoading(true);
          try {
               const res = await fetch(`${BASE_URL}/regularize/${rejectId}/reject`, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         Authorization: token ? `Bearer ${token}` : undefined
                    },
                    body: JSON.stringify({ rejected_by: sessionStorage.getItem('user_code') || null, remarks: rejectRemarks })
               });
               if (!res.ok) {
                    const err = await res.json().catch(() => null);
                    throw new Error(err?.message || 'Reject failed');
               }
               setRejectId(null);
               setRejectRemarks('');
               await fetchRequests(status);
          } catch (err) {
               console.error(err);
               alert(err.message || 'Reject failed');
          } finally { setActionLoading(false); }
     };

     return (
          <div className="container-fluid px-4 mt-4">
               <style>{`
                    .regularize-search-input { width: 100%; box-sizing: border-box; }
                    .reg-req-card .card-body { padding: 0.6rem; }
                    .reg-req-card .reason { white-space: normal; word-break: break-word; color: #555; font-size: 0.92rem; }
                    /* compact circular icon buttons used for actions */
                    .icon-action { width: 34px !important; min-width: 34px !important; height: 34px !important; padding: 0 !important; display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; line-height: 0; font-size: 14px; }
                    .icon-action.btn-sm { width: 32px !important; min-width: 32px !important; height: 32px !important; }
                    .icon-action svg { width: 14px; height: 14px; }
                    .icon-action.btn-success, .icon-action.btn-danger, .icon-action.btn-secondary { padding: 0 !important; }

                    /* Top controls responsive tweaks */
                    .top-controls { flex-wrap: wrap; gap: 0.5rem; }
                    .top-controls .filter-container { display: flex; align-items: center; gap: 0.5rem; }
                    .top-controls .filter-select { width: 130px; }

                    @media (max-width: 767px) {
                         .top-controls { flex-direction: column; align-items: stretch; }
                         .top-controls .filter-container { justify-content: flex-start; }
                         .top-controls .filter-select { width: 120px; }
                         .regularize-search-input { max-width: 100% !important; }
                    }

                    @media (max-width: 480px) { .regularize-search-input { max-width: 100% !important; } }
               `}</style>
               <div className="d-flex justify-content-between align-items-center mb-3 top-controls">
                    <div>
                         <h4 className="mb-0">Regularize Requests</h4>

                    </div>
                    <div className="mt-1 filter-container">
                         <label className="me-2">Filter:</label>
                         <select value={status} onChange={e => setStatus(e.target.value)} className="form-select form-select-sm d-inline-block px-2 filter-select" style={{ width: '130px' }}>
                              <option value="pending">Pending</option>
                              <option value="my">My Requests</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                              <option value="all">All</option>
                         </select>
                    </div>

                    <div className="mt-1 d-flex align-items-center" style={{ gap: 8 }}>
                         <input
                              type="text"
                              className="form-control form-control-sm regularize-search-input"
                              placeholder="Search by name, emp code or reason"
                              value={searchInput}
                              onChange={e => {
                                   const v = e.target.value;
                                   setSearchInput(v);
                                   if (searchDebounce.current) clearTimeout(searchDebounce.current);
                                   searchDebounce.current = setTimeout(() => { setSearch(v); setPage(1); }, 450);
                              }}
                              style={{ minWidth: 0, flex: '1 1 220px', maxWidth: 320 }}
                         />

                         <select value={perPage} onChange={e => { setPerPage(parseInt(e.target.value) || 10); setPage(1); }} className="form-select form-select-sm d-inline-block px-2" style={{ width: '60px' }}>
                              <option value={10}>10 </option>
                              <option value={25}>25 </option>
                              <option value={50}>50 </option>
                              <option value={100}>100 </option>
                         </select>
                    </div>
                    <button className="btn btn-sm btn-secondary icon-action" onClick={() => navigate(-1)} title="Back" aria-label="Back"><FaArrowLeft /></button>
               </div>

                         <>
                         {/* Mobile card list for extra-small screens */}
                         <div className="d-block d-sm-none">
                              {!requests.length ? (
                                   <div className="text-muted">No requests</div>
                              ) : (
                                   requests.map(r => {
                                        const attPath = (r.attachments && r.attachments[0] && (r.attachments[0].file_path || r.attachments[0].path)) || (r.attachment || r.attachment_path || r.attachmentPath);
                                        const nm = (r.attachments && r.attachments[0] && r.attachments[0].original_name) || r.attachment_name || r.original_name || (attPath || '').split('/').pop() || '';
                                        const ext = (nm.split('.').pop() || '').toLowerCase();
                                        const isPdf = ext === 'pdf';

                                        return (
                                             <div className="card mb-2 reg-req-card" key={r.id}>
                                                  <div className="card-body p-2">
                                                       <div className="d-flex justify-content-between align-items-start">
                                                            <div>
                                                                 <div style={{ fontWeight: 700 }}>{formatDate(r.for_date)}</div>
                                                                 <div style={{ fontSize: '0.92rem', color: '#444' }}>
                                                                      {r.emp_code} 
                                                                      <span style={{ marginLeft: 8 }}>{r.employee_name || r.applied_by_name || '-'}</span>
                                                                 </div>
                                                            </div>
                                                            <div style={{ textAlign: 'right' }}>
                                                                 <div style={{ fontWeight: 700 }}>{r.status || '-'}</div>
                                                                 {r.status && r.status.toLowerCase() === 'pending' && canApprove && (
                                                                      <div style={{ fontSize: 11, color: '#6c757d' }}>Actionable</div>
                                                                 )}
                                                            </div>
                                                       </div>

                                                       <div className="mt-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                            <div style={{ minWidth: 48, minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                 {isPdf ? (
                                                                      <div style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #dc3545', borderRadius: 6 }}>
                                                                           <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <polyline points="14 2 14 8 20 8" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <text x="12" y="17" fontSize="6" fill="#dc3545" textAnchor="middle" fontWeight="bold">PDF</text>
                                                                           </svg>
                                                                      </div>
                                                                 ) : (thumbs[r.id] && thumbs[r.id].url) ? (
                                                                      <img src={thumbs[r.id].url} alt={nm} onClick={() => openRequestPreview(r)} title="Preview attachments" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer' }} />
                                                                 ) : (
                                                                      <div style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', borderRadius: 6, border: '1px solid #eee' }}>
                                                                           <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 15V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h4" stroke="#6c757d" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /><path d="M7 9l5 5 5-5" stroke="#6c757d" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                      </div>
                                                                 )}
                                                            </div>

                                                            <div style={{ flex: 1 }}>
                                                                 <div style={{ fontSize: '0.95rem', color: '#333' }}>{r.punch_in ? `In: ${r.punch_in}` : 'In: -'} {r.punch_out ? ` / Out: ${r.punch_out}` : ''}</div>
                                                                 <div style={{ marginTop: 6, fontSize: '0.9rem', color: '#555', whiteSpace: 'normal' }}>{r.reason || '-'}</div>
                                                                 <div className="mt-2 d-flex gap-2 flex-wrap" style={{ alignItems: 'center' }}>
                                                                      {attPath ? (
                                                                           <button type="button" onClick={async () => { if (attPath) await handleDownload(attPath, nm); }} className="btn btn-sm btn-link icon-action" title="Download attachment" aria-label="Download attachment"><FaDownload /></button>
                                                                      ) : (
                                                                           <span className="text-muted">-</span>
                                                                      )}
                                                                      <button type="button" onClick={() => openRequestPreview(r)} className="btn btn-sm btn-secondary icon-action" title="Preview" aria-label="Preview"><FaEye /></button>
                                                                      {canApprove && r.status && r.status.toLowerCase() === 'pending' && (
                                                                           <div style={{ display: 'inline-flex', gap: 6 }}>
                                                                                <button className="btn btn-sm btn-success icon-action" onClick={() => approve(r.id)} disabled={actionLoading} title="Approve" aria-label="Approve"><FaCheck /></button>
                                                                                <button className="btn btn-sm btn-danger icon-action" onClick={() => startReject(r.id)} disabled={actionLoading} title="Reject" aria-label="Reject"><FaTimes /></button>
                                                                           </div>
                                                                      )}
                                                                 </div>
                                                            </div>
                                                       </div>
                                                  </div>
                                             </div>
                                        );
                                   })
                              )}
                         </div>

                         {/* Desktop table (hidden on xs) - simplified columns for stability */}
                         <div className="table-responsive d-none d-sm-block">
                              <table className="table table-sm">
                                   <thead>
                                        <tr>
                                             <th>Date</th>
                                             
                                             <th>Employee</th>
                                             <th>In</th>
                                             <th>Out</th>
                                             <th>Reason</th>
                                             <th>Attachment</th>
                                             <th>Status</th>
                                             <th className="text-end">Actions</th>
                                        </tr>
                                   </thead>
                                   <tbody>
                                        {!requests.length && <tr><td colSpan="4" className="text-muted">No requests</td></tr>}
                                        {requests.map(r => (
                                             <tr key={r.id}>
                                                  <td style={{ verticalAlign: 'middle' }}>{formatDate(r.for_date)}</td>
                                                  <td style={{ verticalAlign: 'middle' }}>
                                                       {r.emp_code} 
                                                       {r.employee_name ? <span style={{ marginLeft: 8 }}>{r.employee_name}</span> : null}
                                                  </td>
                                                 <td style={{ verticalAlign: 'middle' }}>{r.punch_in || '-'}</td>
                                                  <td style={{ verticalAlign: 'middle' }}>{r.punch_out || '-'}</td>
                                                  <td style={{ verticalAlign: 'middle', maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>{r.reason || '-'}</td>
                                                  <td style={{ verticalAlign: 'middle', width: 180 }}>
                                                       {(() => {
                                                            const attPath = (r.attachments && r.attachments[0] && (r.attachments[0].file_path || r.attachments[0].path)) || (r.attachment || r.attachment_path || r.attachmentPath);
                                                            const nm = (r.attachments && r.attachments[0] && r.attachments[0].original_name) || r.attachment_name || r.original_name || (attPath || '').split('/').pop() || '';
                                                            const ext = (nm.split('.').pop() || '').toLowerCase();
                                                            const isPdf = ext === 'pdf';
                                                            if (isPdf) {
                                                                 return (
                                                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                           <div style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #dc3545', borderRadius: 6 }}>
                                                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                     <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                     <polyline points="14 2 14 8 20 8" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                     <text x="12" y="17" fontSize="6" fill="#dc3545" textAnchor="middle" fontWeight="bold">PDF</text>
                                                                                </svg>
                                                                           </div>
                                                                           <div><button type="button" className="btn btn-sm btn-link p-0" onClick={async () => { if (attPath) await handleDownload(attPath, nm); }}>{nm || 'View'}</button></div>
                                                                      </div>
                                                                 );
                                                            }
                                                            if (thumbs[r.id] && thumbs[r.id].url) {
                                                                 return (
                                                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                           <img src={thumbs[r.id].url} alt={nm} onClick={() => openRequestPreview(r)} title={nm || 'Preview attachments'} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer' }} />
                                                                      </div>
                                                                 );
                                                            }
                                                            if (attPath) {
                                                                 return (
                                                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                           <button type="button" className="btn btn-sm btn-link p-1" onClick={async () => { if (attPath) await handleDownload(attPath, nm); }} title="Download attachment" aria-label="Download attachment"><FaDownload /></button>
                                                                      </div>
                                                                 );
                                                            }
                                                            return '-';
                                                       })()}
                                                  </td>
                                                  <td style={{ verticalAlign: 'middle' }}>{r.status || '-'}</td>
                                                  <td className="text-end" style={{ verticalAlign: 'middle' }}>
                                                       <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                            <button className="btn btn-sm btn-secondary icon-action" onClick={() => openRequestPreview(r)} title="Preview" aria-label="Preview"><FaEye /></button>
                                                            {canApprove && r.status && r.status.toLowerCase() === 'pending' && (
                                                                 <div style={{ display: 'inline-flex', gap: 6 }}>
                                                                      <button className="btn btn-sm btn-success icon-action" onClick={() => approve(r.id)} disabled={actionLoading} title="Approve" aria-label="Approve"><FaCheck /></button>
                                                                      <button className="btn btn-sm btn-danger icon-action" onClick={() => startReject(r.id)} disabled={actionLoading} title="Reject" aria-label="Reject"><FaTimes /></button>
                                                                 </div>
                                                            )}
                                                       </div>
                                                  </td>
                                             </tr>
                                        ))}
                                   </tbody>
                              </table>
                         </div>

                         {/* Pagination controls */}
                         <div className="d-flex justify-content-between align-items-center mt-3">
                              <div className="text-muted">
                                   Showing {Math.min(((page - 1) * perPage) + 1, total || 0)} - {Math.min(page * perPage, total || 0)} of <strong>{total}</strong>
                              </div>
                              <div>
                                   <button className="btn btn-sm btn-outline-primary me-2" style={{ padding: '0.22rem 0.4rem', fontSize: '0.82rem', minWidth: 34, height: 30, lineHeight: 1, borderRadius: 6 }} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>

                                   {/* numbered page buttons */}
                                   {(() => {
                                        const pages = [];
                                        const totalToShow = 7; // max buttons to show
                                        let start = Math.max(1, page - Math.floor(totalToShow / 2));
                                        let end = start + totalToShow - 1;
                                        if (end > totalPages) { end = totalPages; start = Math.max(1, end - totalToShow + 1); }

                                        if (start > 1) {
                                             pages.push(<button key="first" className="btn btn-sm btn-outline-secondary me-1" style={{ padding: '0.22rem 0.38rem', fontSize: '0.82rem', minWidth: 30, height: 30 }} onClick={() => setPage(1)}>1</button>);
                                             if (start > 2) pages.push(<span key="dots1" className="mx-1">…</span>);
                                        }

                                        for (let i = start; i <= end; i++) {
                                             pages.push(
                                                  <button key={i} className={`btn btn-sm ${i === page ? 'btn-primary' : 'btn-outline-secondary'} me-1`} style={{ padding: '0.22rem 0.38rem', fontSize: '0.82rem', minWidth: 30, height: 30 }} onClick={() => setPage(i)}>{i}</button>
                                             );
                                        }

                                        if (end < totalPages) {
                                             if (end < totalPages - 1) pages.push(<span key="dots2" className="mx-1">…</span>);
                                             pages.push(<button key="last" className="btn btn-sm btn-outline-secondary ms-1" style={{ padding: '0.22rem 0.38rem', fontSize: '0.82rem', minWidth: 30, height: 30 }} onClick={() => setPage(totalPages)}>{totalPages}</button>);
                                        }

                                        return pages;
                                   })()}

                                   <button className="btn btn-sm btn-outline-primary ms-2" style={{ padding: '0.22rem 0.4rem', fontSize: '0.82rem', minWidth: 34, height: 30, lineHeight: 1, borderRadius: 6 }} disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
                              </div>
                         </div>
                    </>
                

               {/* Reject modal simple */}
               {rejectId && (
                    <div className="modal show d-block" tabIndex="-1" role="dialog" onClick={() => setRejectId(null)}>
                         <div className="modal-dialog" role="document" onClick={e => e.stopPropagation()}>
                              <div className="modal-content">
                                   <div className="modal-header">
                                        <h5 className="modal-title">Reject Request</h5>
                                        <button type="button" className="btn-close" onClick={() => setRejectId(null)} />
                                   </div>
                                   <div className="modal-body">
                                        <textarea className="form-control" rows={4} value={rejectRemarks} onChange={e => setRejectRemarks(e.target.value)} />
                                   </div>
                                   <div className="modal-footer">
                                        <button className="btn btn-secondary" onClick={() => setRejectId(null)}>Cancel</button>
                                        <button className="btn btn-danger" onClick={submitReject} disabled={actionLoading}>Submit Reject</button>
                                   </div>
                              </div>
                         </div>
                    </div>
               )}

               {/* Request preview modal */}
               {previewOpen && selectedRequest && (
                    <div className="modal show d-block" tabIndex="-1" role="dialog" onClick={closeRequestPreview}>
                         <div className="modal-dialog modal-lg modal-dialog-centered" role="document" onClick={e => e.stopPropagation()}>
                              <div className="modal-content">
                                   <div className="modal-header">
                                        <h5 className="modal-title">Request Details</h5>
                                        <button type="button" className="btn-close" onClick={closeRequestPreview} />
                                   </div>
                                   <div className="modal-body p-4">
                                        <div className="mb-3">
                                             <strong>Date:</strong> {formatDate(selectedRequest.for_date)} &nbsp; <strong>Employee:</strong> {selectedRequest.emp_code}
                                        </div>
                                        <div className="mb-3"><strong>In:</strong> {selectedRequest.punch_in || '-'} &nbsp; <strong>Out:</strong> {selectedRequest.punch_out || '-'}</div>
                                        <div className="mb-3"><strong>Reason:</strong><div style={{ marginTop: 6 }}>{selectedRequest.reason || '-'}</div></div>

                                        <div className="mb-3">
                                             <strong>Attachments</strong>
                                             <div style={{ marginTop: 8 }}>
                                                  {previewLoading ? (
                                                       <div className="text-muted">Loading attachments…</div>
                                                  ) : Array.isArray(selectedRequest?.attachments) && selectedRequest.attachments.length ? (
                                                       selectedRequest.attachments.map((a, idx) => (
                                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                                                 {previewThumbs[idx] && previewThumbs[idx].url ? (
                                                                      <img src={previewThumbs[idx].url} alt={a.original_name || ''} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
                                                                 ) : previewThumbs[idx] && previewThumbs[idx].failed ? (
                                                                      <div style={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ddd', borderRadius: 6, background: '#fff' }}>
                                                                           <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 15V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h4" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M7 9l5 5 5-5" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                      </div>
                                                                 ) : (
                                                                      <div style={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                                                                           <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                      </div>
                                                                 )}
                                                                 <div style={{ lineHeight: 1 }}>
                                                                      <div style={{ fontWeight: 600 }}>{a.original_name || a.filename || (a.file_path || '').split('/').pop()}</div>
                                                                      <div className="mt-1"><button type="button" onClick={async () => { await handleDownload(a.file_path || a.path || a.attachment_path || a.attachment, a.original_name || a.filename); }} className="btn btn-link link-primary p-0">View</button></div>
                                                                 </div>
                                                            </div>
                                                       ))
                                                  ) : (selectedRequest && (selectedRequest.attachment || selectedRequest.attachment_path || selectedRequest.attachmentPath)) ? (
                                                       <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                            {previewThumbs[0] && previewThumbs[0].url ? (
                                                                 <img src={previewThumbs[0].url} alt={selectedRequest.attachment_name || selectedRequest.original_name || ''} style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
                                                            ) : previewThumbs[0] && previewThumbs[0].failed ? (
                                                                 <div style={{ width: 120, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ddd', borderRadius: 6, background: '#fff' }}>
                                                                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 15V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h4" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M7 9l5 5 5-5" stroke="#6c757d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                 </div>
                                                            ) : (
                                                                 <div style={{ width: 120, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                                                                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                 </div>
                                                            )}
                                                            <div style={{ lineHeight: 1 }}>
                                                                 <div style={{ fontWeight: 600 }}>{selectedRequest.attachment_name || selectedRequest.original_name || ((selectedRequest.attachment || selectedRequest.attachment_path || '').split('/').pop())}</div>
                                                                 <div className="mt-1"><button type="button" onClick={async () => { await handleDownload((selectedRequest.attachment || selectedRequest.attachment_path || selectedRequest.attachmentPath), (selectedRequest.attachment_name || selectedRequest.original_name || (selectedRequest.attachment || selectedRequest.attachment_path || '').split('/').pop())); }} className="btn btn-link link-primary p-0">View</button></div>
                                                            </div>
                                                       </div>
                                                  ) : <div className="text-muted">No attachments</div>}
                                             </div>
                                        </div>
                                   </div>
                                   <div className="modal-footer">
                                        {(selectedRequest.emp_code === userCode) && (
                                             <button className="btn btn-secondary" onClick={() => navigate(`/regularize-request?id=${selectedRequest.id}&edit=1`)}>Edit</button>
                                        )}
                                        {canApprove && (
                                             <>
                                                  <button className="btn btn-success" onClick={() => approveAndClose(selectedRequest.id)} disabled={actionLoading}>Approve</button>
                                                  <button className="btn btn-danger" onClick={() => rejectFromPreview(selectedRequest.id)} disabled={actionLoading}>Reject</button>
                                             </>
                                        )}
                                        <button className="btn btn-secondary" onClick={closeRequestPreview}>Close</button>
                                   </div>
                              </div>
                         </div>
                    </div>
               )}

               {/* Viewer modal */}
               {viewerOpen && (
                    <div className="modal show d-block" tabIndex={-1} role="dialog" onClick={closeViewer}>
                         <div className="modal-dialog modal-xl modal-dialog-centered" role="document" onClick={e => e.stopPropagation()}>
                              <div className="modal-content">
                                   <div className="modal-header">
                                        <h5 className="modal-title">{viewerName}</h5>
                                        <button type="button" className="btn-close" onClick={closeViewer}></button>
                                   </div>
                                   <div className="modal-body" style={{ minHeight: 300 }}>
                                        {viewerContentType.startsWith('image/') ? (
                                             <div style={{ textAlign: 'center', background: '#111' }}>
                                                  <img src={viewerUrl} alt={viewerName} style={{ maxWidth: '100%', height: 'auto' }} />
                                             </div>
                                        ) : (
                                             <iframe src={viewerUrl} title={viewerName} style={{ width: '100%', height: '70vh', border: 0 }} />
                                        )}
                                   </div>
                                   <div className="modal-footer">
                                        <button className="btn btn-secondary" onClick={closeViewer}>Close</button>
                                        <a className="btn btn-primary" href={viewerUrl} download={viewerName}>Download</a>
                                   </div>
                              </div>
                         </div>
                    </div>
               )}
          </div>
     );
}